<template>
  <div class="container-fluid mt-4">
    <div class="d-flex justify-content-between align-items-center mb-5">
      <h2 class="fw-bold text-dark mb-0">Locations <span class="text-muted fs-5">({{ locations.length }})</span></h2>
      <div class="d-flex align-items-center gap-3">
        <b-form-input v-model="searchTerm" placeholder="Search locations..." class="form-control bg-white shadow-sm border-0 rounded-pill px-4" style="width: 300px;"></b-form-input>
        <button class="btn btn-primary-gradient px-4 rounded-pill shadow-sm" @click="openAddModal">
          <i class="fa fa-plus me-2"></i> Add Location
        </button>
      </div>
    </div>

    <div class="glass-panel overflow-hidden">
      <b-table-simple striped hover class="mb-0">
        <b-thead class="bg-light">
          <b-tr>
            <b-th class="border-0 fw-bold text-muted py-3 px-4">Name</b-th>
            <b-th class="border-0 fw-bold text-muted py-3 px-4">Latitude</b-th>
            <b-th class="border-0 fw-bold text-muted py-3 px-4">Longitude</b-th>
            <b-th class="border-0 fw-bold text-muted py-3 px-4">Address</b-th>
            <b-th class="border-0 fw-bold text-muted py-3 px-4 text-end">Actions</b-th>
          </b-tr>
        </b-thead>
        <b-tbody>
          <b-tr v-for="location in filteredLocations" :key="location.id" class="align-middle">
            <b-td class="px-4 fw-bold text-dark">
              {{ location.attributes.name }}
            </b-td>
            <b-td class="px-4 text-muted">{{ location.attributes.latitude }}</b-td>
            <b-td class="px-4 text-muted">{{ location.attributes.longitude }}</b-td>
            <b-td class="px-4 text-muted">{{ location.attributes.address }}</b-td>
            <b-td class="px-4 text-end">
              <button class="btn btn-light btn-sm rounded-pill text-primary px-3 fw-bold me-2" @click="editLocation(location)">
                <i class="fa fa-pen me-1"></i> Edit
              </button>
              <button class="btn btn-light btn-sm rounded-pill text-danger px-3 fw-bold" @click="promptDelete(location.id)">
                <i class="fa fa-trash me-1"></i> Delete
              </button>
            </b-td>
          </b-tr>
          <b-tr v-if="filteredLocations.length === 0">
            <b-td colspan="5" class="text-center py-5 text-muted">No locations found.</b-td>
          </b-tr>
        </b-tbody>
      </b-table-simple>
    </div>

    <!-- Add/Edit Modal -->
    <b-modal id="location-modal" :title="isEditing ? 'Edit Location' : 'Add Location'" hide-footer centered header-class="border-0 pb-0" body-class="p-4" content-class="border-0 shadow-lg rounded-4">
      <form @submit.prevent="handleSubmit">
        <div class="mb-3">
          <label class="form-label text-muted small fw-bold mb-2">Name</label>
          <input type="text" v-model="form.name" class="form-control bg-light border-0" required placeholder="e.g. Alexanderplatz" />
        </div>
        <div class="mb-3">
          <label class="form-label text-muted small fw-bold mb-2">Address</label>
          <input type="text" v-model="form.address" class="form-control bg-light border-0" required placeholder="Full address" />
        </div>
        <div class="row">
          <div class="col-6 mb-4">
            <label class="form-label text-muted small fw-bold mb-2">Latitude</label>
            <input type="text" v-model="form.latitude" class="form-control bg-light border-0" required placeholder="e.g. 52.5200" />
          </div>
          <div class="col-6 mb-4">
            <label class="form-label text-muted small fw-bold mb-2">Longitude</label>
            <input type="text" v-model="form.longitude" class="form-control bg-light border-0" required placeholder="e.g. 13.4050" />
          </div>
        </div>
        <div class="d-flex justify-content-end mt-2">
          <button type="button" class="btn btn-light rounded-pill px-4 me-3 fw-bold" @click="$bvModal.hide('location-modal')">Cancel</button>
          <button type="submit" class="btn btn-primary-gradient rounded-pill px-4 shadow-sm" :disabled="loading">
            <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
            {{ isEditing ? 'Save Changes' : 'Create Location' }}
          </button>
        </div>
      </form>
    </b-modal>

    <!-- Delete Confirmation Modal -->
    <b-modal id="delete-confirm-modal" title="Confirm Deletion" hide-footer centered header-class="border-0 pb-0" body-class="p-4 text-center" content-class="border-0 shadow-lg rounded-4">
      <div class="mb-4">
        <i class="fa fa-exclamation-triangle text-danger mb-3" style="font-size: 3rem;"></i>
        <h4 class="fw-bold text-dark mb-2">Delete Location?</h4>
        <p class="text-muted">This action cannot be undone. Are you sure you want to permanently delete this location?</p>
      </div>
      <div class="d-flex justify-content-center gap-3 mt-4">
        <button type="button" class="btn btn-light rounded-pill px-4 fw-bold" @click="$bvModal.hide('delete-confirm-modal')">Cancel</button>
        <button type="button" class="btn btn-danger rounded-pill px-4 fw-bold shadow-sm" @click="confirmDelete" :disabled="loading">
          <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
          Yes, Delete
        </button>
      </div>
    </b-modal>
  </div>
</template>

<script>
export default {
  data() {
    return {
      locations: [],
      searchTerm: '',
      isEditing: false,
      loading: false,
      locationToDelete: null,
      form: {
        id: null,
        name: '',
        latitude: '',
        longitude: '',
        address: ''
      }
    };
  },
  async mounted() {
    await this.getData();
  },
  computed: {
    filteredLocations() {
      if (!this.searchTerm) return this.locations;
      const term = this.searchTerm.toLowerCase();
      return this.locations.filter(loc => 
        (loc.attributes.name && loc.attributes.name.toLowerCase().includes(term)) ||
        (loc.attributes.address && loc.attributes.address.toLowerCase().includes(term))
      );
    }
  },
  methods: {
    async getData() {
      try {
        const result = await this.$xhr.get("/api/sk-locations");
        this.locations = result.data.data;
      } catch (error) {
        console.error("Error fetching locations", error);
      }
    },
    openAddModal() {
      this.isEditing = false;
      this.form = { id: null, name: '', latitude: '', longitude: '', address: '' };
      this.$bvModal.show('location-modal');
    },
    editLocation(location) {
      this.isEditing = true;
      this.form = {
        id: location.id,
        name: location.attributes.name,
        latitude: location.attributes.latitude,
        longitude: location.attributes.longitude,
        address: location.attributes.address
      };
      this.$bvModal.show('location-modal');
    },
    async handleSubmit() {
      this.loading = true;
      try {
        const payload = { data: { 
          name: this.form.name, 
          latitude: this.form.latitude, 
          longitude: this.form.longitude, 
          address: this.form.address 
        }};

        if (this.isEditing) {
          await this.$xhr.put(`/api/sk-locations/${this.form.id}`, payload);
          this.$bvToast.toast('Location updated!', { title: 'Success', variant: 'success', solid: true });
        } else {
          await this.$xhr.post("/api/sk-locations", payload);
          this.$bvToast.toast('Location created!', { title: 'Success', variant: 'success', solid: true });
        }
        await this.getData();
        this.$bvModal.hide('location-modal');
      } catch (error) {
        console.error(error);
        this.$bvToast.toast('Operation failed', { title: 'Error', variant: 'danger', solid: true });
      } finally {
        this.loading = false;
      }
    },
    promptDelete(id) {
      this.locationToDelete = id;
      this.$bvModal.show('delete-confirm-modal');
    },
    async confirmDelete() {
      if (!this.locationToDelete) return;
      this.loading = true;
      try {
        await this.$xhr.delete(`/api/sk-locations/${this.locationToDelete}`);
        this.locations = this.locations.filter(loc => loc.id !== this.locationToDelete);
        this.$bvToast.toast('Location deleted', { title: 'Deleted', variant: 'warning', solid: true });
        this.$bvModal.hide('delete-confirm-modal');
      } catch (error) {
        console.error(error);
        this.$bvToast.toast('Delete failed', { title: 'Error', variant: 'danger', solid: true });
      } finally {
        this.loading = false;
        this.locationToDelete = null;
      }
    }
  }
};
</script>

<style scoped>
.hover-lift {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.hover-lift:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0,0,0,0.08) !important;
}
.btn-light {
  background-color: #F8F9FC;
  border: 1px solid #E2E2EA;
}
.btn-light:hover {
  background-color: #E2E2EA;
}
.rounded-4 {
  border-radius: 20px !important;
}
.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>